import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Loader2, ImageIcon } from "lucide-react";
import { api, formatPrice, formatApiErrorDetail } from "../lib/api";

const EMPTY = {
  title: "", artist_name: "", artist_bio: "", description: "", price: "",
  image_url: "", category: "Abstrait", technique: "", dimensions: "", year: "", sold: false,
};
const CATEGORIES = ["Abstrait", "Expressionnisme", "Moderne", "Peinture"];

export const ArtworkManager = ({ defaultArtistName = "" }) => {
  const [artworks, setArtworks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/my/artworks").then((r) => setArtworks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ ...EMPTY, artist_name: defaultArtistName }); setEditId(null); setModalOpen(true); };
  const openEdit = (a) => { setForm({ ...a, price: String(a.price) }); setEditId(a.id); setModalOpen(true); };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: res.data.image_url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !form.image_url) {
      toast.error("Titre, prix et image sont obligatoires");
      return;
    }
    setSaving(true);
    const payload = { ...form, artist_name: form.artist_name || defaultArtistName, price: parseFloat(form.price) };
    try {
      if (editId) {
        await api.put(`/artworks/${editId}`, payload);
        toast.success("Œuvre mise à jour");
      } else {
        await api.post("/artworks", payload);
        toast.success("Œuvre ajoutée");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette œuvre ?")) return;
    await api.delete(`/artworks/${id}`);
    toast.success("Œuvre supprimée");
    load();
  };

  return (
    <div>
      <button
        data-testid="add-artwork-button"
        onClick={openCreate}
        className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm mb-8 transition-colors"
      >
        <Plus className="h-4 w-4" /> Ajouter une œuvre
      </button>

      {artworks.length === 0 ? (
        <p className="text-zinc-500">Aucune œuvre pour le moment. Ajoutez votre première création.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {artworks.map((a) => (
            <div key={a.id} className="border border-white/10 bg-[#0d0d0d]" data-testid={`admin-artwork-${a.id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={a.image_url} alt={a.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl text-white">{a.title}</h3>
                  {a.sold && <span className="text-[#E11D48] text-xs uppercase">Vendu</span>}
                </div>
                <p className="text-zinc-500 text-sm">{a.artist_name}</p>
                <p className="text-zinc-300 text-sm mt-1">{formatPrice(a.price)}</p>
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid={`edit-artwork-${a.id}`}
                    onClick={() => openEdit(a)}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white py-2 text-sm transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Modifier
                  </button>
                  <button
                    data-testid={`delete-artwork-${a.id}`}
                    onClick={() => remove(a.id)}
                    className="px-3 border border-white/15 hover:border-[#E11D48] hover:text-[#E11D48] text-white transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="artwork-modal">
          <div className="bg-[#0d0d0d] border border-white/15 w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="font-display text-2xl text-white">{editId ? "Modifier l'œuvre" : "Nouvelle œuvre"}</h2>
              <button onClick={() => setModalOpen(false)} className="text-zinc-400 hover:text-white" data-testid="close-modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="h-28 w-28 shrink-0 bg-black border border-white/15 flex items-center justify-center overflow-hidden">
                  {form.image_url ? <img src={form.image_url} alt="aperçu" className="h-full w-full object-cover" /> : <ImageIcon className="h-8 w-8 text-zinc-700" />}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2.5 text-sm cursor-pointer transition-colors" data-testid="upload-image-label">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Téléverser une photo
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="upload-image-input" />
                  </label>
                  <input
                    data-testid="form-image-url"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="ou collez une URL d'image"
                    className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm mt-3 focus:border-[#2563EB] outline-none"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field testid="form-title" label="Titre *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <Field testid="form-artist" label="Artiste" value={form.artist_name} onChange={(v) => setForm({ ...form, artist_name: v })} />
                <Field testid="form-price" label="Prix (€) *" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Catégorie</label>
                  <select
                    data-testid="form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Field testid="form-technique" label="Technique" value={form.technique} onChange={(v) => setForm({ ...form, technique: v })} />
                <Field testid="form-dimensions" label="Dimensions" value={form.dimensions} onChange={(v) => setForm({ ...form, dimensions: v })} />
                <Field testid="form-year" label="Année" value={form.year} onChange={(v) => setForm({ ...form, year: v })} />
              </div>

              <Textarea testid="form-description" label="Description de l'œuvre" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />

              <label className="flex items-center gap-3 text-sm text-zinc-300">
                <input type="checkbox" data-testid="form-sold" checked={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.checked })} className="accent-[#2563EB]" />
                Marquer comme vendue
              </label>

              <button
                data-testid="save-artwork-button"
                type="submit"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white py-3 text-sm transition-colors"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editId ? "Enregistrer" : "Ajouter l'œuvre"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none"
    />
  </div>
);

const Textarea = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none resize-none"
    />
  </div>
);
